import * as Turbo from "@hotwired/turbo"
import { Controller } from "stimulus"

export default class extends Controller {
  static get targets() {
    return ["applePay", "form", "name", "email", "phone", "instructions", "error", "label", "loading", "submit", "total"]
  }

  secret = ""
  card = {}

  connect = () => {
    this._initializePaymentRequest()
    this._initializeStripeElements()
  }

  /**
   * change sends the form data to the server on the unblur event.
   */
  change = () => {
    const data = {
      name: this.nameTarget.value,
      email: this.emailTarget.value,
      phone: this.phoneTarget.value,
      instructions: this.phoneTarget.value
    }

    fetch("/order/user", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    }).catch((err) => {
      console.log("error:", err)
      throw err
    })
  }

  /**
   * pay submits the payment to Stripe to complete the order.
   */
  pay = (ev) => {
    ev.preventDefault()

    const form = this.formTarget

    // Disable the submit button while the form is being submittted.
    this.submitTarget.disabled = true
    this.submitTarget.classList.add("opacity-80")

    this.labelTarget.classList.add("hidden")
    // Clear the error, if one exists.
    this.errorTarget.classList.add("hidden")

    this.loadingTarget.classList.remove("hidden")

    // Check to see if the order is zero dollars. If it is, then just submit
    // the form without doing anything else.
    if (this.totalTarget.value === "0") {
      Turbo.navigator.submitForm(form)
      return
    }

    this._fetchPaymentIntent({
      name: this.nameTarget.value,
      email: this.emailTarget.value,
      phone: this.phoneTarget.value,
      instructions: this.instructionsTarget.value
    }).then(() => {
      stripe.confirmCardPayment(this.secret, {
        payment_method: {
          card: this.card,
          billing_details: {
            address: {
              city: "Thunder Bay",
              country: "CA",
              state: "Ontario"
            },
            email: this.emailTarget.value,
            name: this.nameTarget.value
          }
        }
      }).then((result) => {
        // Re-enable the submit button and show things again.
        this.submitTarget.disabled = false
        this.submitTarget.classList.remove("opacity-80")
        this.labelTarget.classList.remove("hidden")
        this.loadingTarget.classList.add("hidden")

        if (result.error) {
          this.errorTarget.innerText = result.error.message
          this.errorTarget.classList.remove("hidden")
        } else {
          // The payment succeeded!
          this.labelTarget.innerText = "Completed"

          // Submit the form via Turbo.
          Turbo.navigator.submitForm(form)
        }
      })
    })
  }

  // "Private"
  _initializePaymentRequest = _ => {
    const form = this.formTarget
    const errTarget = this.errorTarget
    const self = this

    // Create the payment request based on the order total. A user would need
    // to MITM themselves in order to tamper with the order total, so I'm
    // considering this to be safe for now.
    const paymentRequest = stripe.paymentRequest({
      country: "CA",
      currency: "cad",
      total: {
        label: "Your Sarah and Dipity's order total",
        amount: parseInt(this.totalTarget.value),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    })

    const elements = stripe.elements()
    const prButton = elements.create("paymentRequestButton", {
      paymentRequest: paymentRequest,
    })

    // Check the availability of the Payment Request API first. If it's
    // enabled, show the payment request button.
    paymentRequest.canMakePayment().then(function(result) {
      if (result) {
        self.applePayTarget.classList.remove("hidden")
        prButton.mount("#payment-request-button")
      }
    })

    // Handle completion of the payment method.
    paymentRequest.on("paymentmethod", function(ev) {
      // Clear the error, if one exists.
      errTarget.classList.add("hidden")

      // Confirm the PaymentIntent without handling potential next actions (yet).
      self._fetchPaymentIntent({
        name: ev.payerName,
        email: ev.payerEmail
      }).then(() => {
        self.nameTarget.value = ev.payerName
        self.emailTarget.value = ev.payerEmail

        stripe.confirmCardPayment(
          self.secret,
          {payment_method: ev.paymentMethod.id},
          {handleActions: false}
        ).then(function(confirmResult) {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            ev.complete("fail")
            errTarget.innerText = "Failed to complete payment. Please enter your credit card instead."
            errTarget.classList.remove("hidden")
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            ev.complete("success")
            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11"
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (confirmResult.paymentIntent.status === "requires_action") {
              // Let Stripe.js handle the rest of the payment flow.
              stripe.confirmCardPayment(self.secret).then(function(result) {
                if (result.error) {
                  // The payment failed -- ask your customer for a new payment method.
                  //
                  // todo add msg to say to use manual card
                  errTarget.innerText = result.error.message
                  errTarget.classList.remove("hidden")
                } else {
                  // The payment has succeeded, so submit the form via Turbo.
                  Turbo.navigator.submitForm(form)
                }
              })
            } else {
              // The payment has succeeded, so submit the form via Turbo.
              Turbo.navigator.submitForm(form)
            }
          }
        })
      })
    })
  }

  // Sets up Stripe.js and Elements to use in checkout form.
  _initializeStripeElements = _ => {
    const elements = stripe.elements();

    const card = elements.create("card", {
      style: {
        base: {
          color: "#32325d",
        }
      }
    })

    card.on("change", ({error}) => {
      let displayError = document.getElementById("card-errors");
      if (error) {
        displayError.textContent = error.message
      } else {
        displayError.textContent = ""
      }
    })

    card.mount("#card-element")
    this.card = card
  }

  /**
   * handleErr is a generic error handler that will reset the form and display
   * errors in the error div.
   */
  _handleErr = (response) => {
    // TODO
    //
    // For some reason, the response is always NOT ok.
    //
    // This doesn't make sense.
    if (!response.ok) {
      if (response.error) {
        this.submitTarget.disabled = false
        this.submitTarget.classList.remove("opacity-80")
        this.labelTarget.classList.remove("hidden")
        this.loadingTarget.classList.add("hidden")

        this.errorTarget.innerText = response.message
        this.errorTarget.classList.remove("hidden")
        throw response.error
      }
    }

    return response
  }

  /**
   * _fetchPaymentIntent fetches a Stripe payment intent for the current order
   * from the server.
   *
   */
  _fetchPaymentIntent = (user) => {

    return fetch("/order/intent", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(user)
    }).then((result) => { return result.json() })
    .then(this._handleErr)
    .then((data) => {
      this.secret = data.client_secret
    }).catch((err) => {
      console.log("error:", err)
    })
  }
}
